import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone, Droplets } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import EnquiryForm from "@/components/site/EnquiryForm";
import Testimonials from "@/components/site/Testimonials";

export default function SublimationPage() {
  const [content, setContent] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => setFailed(true));
  }, []);

  const sub = content?.sublimation;

  useEffect(() => {
    if (!sub) return;
    document.title = "Sublimation Printing UK | All-Over Dye Sublimation Process — AZ Concepts";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "See how AZ Concepts produces all-over sublimation printing: artwork, digital print, 200°C heat transfer, cut & sew. Permanent prints that never crack, peel or fade."
      );
  }, [sub]);

  const scrollToEnquiry = () => document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" });

  if (failed)
    return (
      <div className="min-h-screen bg-[#0D0F12] text-slate-300 flex items-center justify-center font-body" data-testid="sublimation-load-error">
        Unable to load right now. Please refresh.
      </div>
    );

  if (!content || !sub)
    return (
      <div className="min-h-screen bg-[#0D0F12] flex items-center justify-center" data-testid="sublimation-loading">
        <div className="h-10 w-10 rounded-full border-2 border-[#FF5A1F] border-t-transparent animate-spin" />
      </div>
    );

  return (
    <div id="top" className="bg-[#0D0F12] text-slate-100 min-h-screen font-body" data-testid="sublimation-page">
      <Header contact={content.contact} onQuote={scrollToEnquiry} products={content.products} />
      <main>
        <section className="max-w-7xl mx-auto px-6 pt-12 lg:pt-20 pb-16" data-testid="sublimation-hero">
          <Link to="/" data-testid="sublimation-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]" data-testid="sublimation-eyebrow">
            The Sublimation Process
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none max-w-4xl"
            data-testid="sublimation-title">
            {sub.hero_title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed" data-testid="sublimation-subtitle">
            {sub.hero_subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4">
            <button data-testid="sublimation-quote-button" onClick={scrollToEnquiry}
              className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04810] text-white font-bold rounded-full px-7 py-3.5 transition-all hover:gap-3 shadow-lg shadow-[#FF5A1F]/25">
              Start a Sublimation Order <ArrowRight size={17} />
            </button>
            <a data-testid="sublimation-call-button" href={`tel:${content.contact.phone}`}
              className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 text-white font-semibold rounded-full px-7 py-3.5 transition-colors">
              <Phone size={17} /> Call {content.contact.phone}
            </a>
          </motion.div>
        </section>

        <section className="bg-[#161B22]/50 border-y border-white/5" data-testid="sublimation-intro-section">
          <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-11 w-11 rounded-xl bg-[#FF5A1F]/15 flex items-center justify-center">
                  <Droplets size={20} className="text-[#FF5A1F]" />
                </span>
                <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Why sublimation wins</h2>
              </div>
              <p className="mt-6 text-base text-slate-300 leading-relaxed" data-testid="sublimation-intro">{sub.intro}</p>
            </div>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-video" data-testid="sublimation-video-wrap">
              {content.hero.video_url ? (
                <video src={content.hero.video_url} autoPlay muted loop playsInline poster={content.hero.image}
                  className="w-full h-full object-cover" data-testid="sublimation-video" />
              ) : (
                <img src={content.hero.image} alt="Sublimation printing facility" className="w-full h-full object-cover animate-kenburns" />
              )}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 py-20 lg:py-28" data-testid="sublimation-steps-section">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">From artwork to doorstep</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="sublimation-steps-heading">
            How your order is made
          </h2>
          <div className="mt-12 space-y-6">
            {sub.steps.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="flex gap-6 bg-[#161B22] border border-white/10 rounded-2xl p-6 lg:p-8 items-start"
                data-testid={`sublimation-step-${i}`}>
                <span className="font-heading text-4xl lg:text-5xl font-extrabold text-[#FF5A1F]/30 shrink-0">0{i + 1}</span>
                <div>
                  <h3 className="font-heading text-xl font-bold">{s.title}</h3>
                  <p className="mt-2 text-sm sm:text-base text-slate-400 leading-relaxed">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {content.gallery?.length > 0 && (
          <section className="bg-[#161B22]/50 border-y border-white/5" data-testid="sublimation-examples-section">
            <div className="max-w-7xl mx-auto px-6 py-20">
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A1F]">Examples</p>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="sublimation-examples-heading">
                Recent work from our workshop
              </h2>
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {content.gallery.map((g, i) => (
                  <motion.figure key={g.id}
                    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
                    className="group bg-[#161B22] border border-white/10 rounded-2xl overflow-hidden"
                    data-testid={`sublimation-example-${g.id}`}>
                    <div className="h-56 overflow-hidden">
                      <img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <figcaption className="p-5">
                      <p className="font-heading font-bold">{g.title}</p>
                      <p className="mt-1 text-sm text-slate-400">{g.caption}</p>
                    </figcaption>
                  </motion.figure>
                ))}
              </div>
            </div>
          </section>
        )}

        <Testimonials items={content.testimonials} />
        <EnquiryForm products={content.products} contact={content.contact} productInterest="sportswear" />
      </main>
      <Footer contact={content.contact} products={content.products} />
      <FloatingCTA contact={content.contact} />
    </div>
  );
}
